<template>
  <ActionButton
    class="shipment-financials-action-button"
    mode="secondary"
    :actions="actions"
    :loading="shipmentFinancials.loading"
  />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import axios from 'axios';
import { computed, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { Loadable, RygenMenuItem } from '@rygen/rygen-ui/model';
import { ShipmentFinancials } from '@rygen/rygen-ui/model/shipmentFinancials';

import api from '@/api';
import { ShipmentFinancialsAction } from '@/model';

import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';

const props = defineProps({
  shipmentFinancials: {
    type: Object as PropType<Loadable<ShipmentFinancials>>,
    default: () => ({ loading: false }),
  },
});
const { shipmentFinancials } = toRefs(props);
const emit = defineEmits<{
  (event: 'refresh'): void;
  (event: 'view-cost-allocation'): void;
  (event: 'create-invoice'): void;
}>();

const { t } = useI18n();

const hasCreateInvoicePermission = ref(false);

const canViewCostAllocation = computed(
  () =>
    authorization.hasAuthority('shipment', 'readCostAllocation') &&
    !shipmentFinancials.value.error &&
    (shipmentFinancials.value.data?.jobCostingLedger?.clientCostDetails
      ?.length ?? 0) > 0,
);

const canViewCreateInvoice = computed(
  () =>
    hasCreateInvoicePermission.value &&
    shipmentFinancials.value.data?.status === 'NEW',
);

const actions: Ref<RygenMenuItem[]> = computed(() => [
  {
    key: ShipmentFinancialsAction.COST_ALLOCATION,
    label: t('enumMap.ShipmentFinancialsAction.COST_ALLOCATION'),
    command: () => emit('view-cost-allocation'),
    visible: canViewCostAllocation.value,
  },
  {
    key: ShipmentFinancialsAction.CREATE_INVOICE,
    label: t('enumMap.ShipmentFinancialsAction.CREATE_INVOICE'),
    command: () => emit('create-invoice'),
    visible: canViewCreateInvoice.value,
  },
  {
    key: ShipmentFinancialsAction.REFRESH,
    label: t('enumMap.ShipmentFinancialsAction.REFRESH'),
    icon: PrimeIcons.REFRESH,
    command: () => emit('refresh'),
    visible: true,
  },
]);

const getCreateInvoiceShipmentPermission = async (shipmentId: number) => {
  try {
    hasCreateInvoicePermission.value =
      await api.user.getCreateInvoiceShipmentPermission(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error fetching create invoice permission for shipment ${shipmentId}`,
      e,
    );
    hasCreateInvoicePermission.value = false;
  }
};

watch(
  () => shipmentFinancials.value.data?.sourceSystemId,
  (newVal: string | undefined) => {
    if (newVal) {
      const shipmentId = Number.parseInt(newVal);
      getCreateInvoiceShipmentPermission(shipmentId);
    } else {
      hasCreateInvoicePermission.value = false;
    }
  },
  { immediate: true },
);
</script>
